import { useEffect } from "react";

import { useLanguage } from "@/contexts/LanguageContext";
import { PageContainer } from "@/components/flowly/PageContainer/PageContainer";
import { useInView } from "@/hooks/useInView";
import { cn } from "@/lib/utils";
import { captureEvent } from "@/lib/analyticsQueue";
import { ANALYTICS_EVENTS } from "@shared/schemas/analytics";
import { useCommunityCta } from "@/components/flowly/WhatsAppCommunity/useCommunityCta";
import { WhatsAppIcon } from "@/components/flowly/WhatsAppCommunity/WhatsAppIcon";

/**
 * Landing-page community banner. Built in the same visual idiom as the other
 * landing sections (eyebrow + bold heading + muted subtitle, in-view reveal,
 * soft glow atmosphere) with WhatsApp-green as the single sharp accent.
 */
export function LandingCommunity() {
  const { t } = useLanguage();
  const { ref, isInView } = useInView();
  const { getCommunityHref, openCommunity } = useCommunityCta();

  useEffect(() => {
    captureEvent(ANALYTICS_EVENTS.COMMUNITY_CTA_VIEW, { source: "landing" });
  }, []);

  return (
    <section className="py-20 md:py-28">
      <PageContainer width="wide">
        <div
          ref={ref}
          className={cn(
            "relative overflow-hidden rounded-3xl border border-border bg-card",
            "px-6 py-14 sm:px-12 md:px-16 md:py-20",
            "transition-all duration-700",
            isInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8",
          )}
        >
          {/* Soft WhatsApp-green atmosphere */}
          <div
            aria-hidden
            className="pointer-events-none absolute -top-24 -end-24 h-72 w-72 rounded-full bg-[#25D366]/20 blur-[120px]"
          />
          <div
            aria-hidden
            className="pointer-events-none absolute -bottom-28 -start-20 h-72 w-72 rounded-full bg-[#25D366]/10 blur-[130px]"
          />

          <div className="relative flex flex-col items-center text-center gap-6">
            {/* Icon badge */}
            <div className="flex items-center justify-center w-16 h-16 md:w-20 md:h-20 rounded-2xl bg-[#25D366] shadow-lg shadow-[#25D366]/40">
              <WhatsAppIcon className="w-8 h-8 md:w-9 md:h-9 text-white" />
            </div>

            <div className="space-y-3 max-w-2xl">
              <span className="block text-xs font-semibold tracking-[0.2em] uppercase text-[#25D366]">
                {t.community.eyebrow}
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-foreground">
                {t.community.title}
              </h2>
              <p className="text-lg text-muted-foreground leading-relaxed">
                {t.community.description}
              </p>
            </div>

            <a
              href={getCommunityHref()}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => openCommunity("landing")}
              className="inline-flex items-center gap-2.5 rounded-full bg-[#25D366] px-7 py-3.5 text-base font-semibold text-white shadow-lg shadow-[#25D366]/40 transition-all hover:-translate-y-0.5 hover:bg-[#1fba59]"
            >
              <WhatsAppIcon className="w-5 h-5" />
              {t.community.joinButton}
            </a>

            <span className="text-sm text-muted-foreground">
              {t.community.quietNote}
            </span>
          </div>
        </div>
      </PageContainer>
    </section>
  );
}
